'use client';

import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import classNames from 'classnames';
import { useState } from 'react';
import { useEffect } from 'react';
import List from 'slices/List';
import TableWithColumns from 'slices/TableWithColumns';
import EventNames from 'src/analytics/EventNames';
import { trackClick } from 'src/analytics/Events';
import { Badge, Button } from '@get-mosh/msh-shared';
import Heading from 'src/components/Heading/Heading';
import { Content } from '@prismicio/client';
import { ProductDocumentDataSlicesSlice } from 'prismicio-types';

import styles from './hero-product.module.scss';
import { FilledContentRelationshipField } from '@prismicio/types';
import { PrismicNextImage } from '@prismicio/next';

type HeroProductProps = SliceComponentProps<Content.HeroProductSlice> & {
    context: {
        pageType: string;
    };
};

const HeroProduct = ({ slice, context }: HeroProductProps) => {
    const [productData, setProductData] = useState<Content.ProductDocument[]>();
    const tabActiveDefault = () => {
        const tabActive = slice.primary.tab_active
            ? slice.primary.tab_active - 1
            : 0;
        return tabActive >= 0 && tabActive < slice.items.length ? tabActive : 0;
    };
    const [tabActive, setTabActive] = useState(tabActiveDefault);

    useEffect(() => {
        if (!slice.items || !slice.items.length) return;
        const requestOptions = {
            method: 'POST',
            body: JSON.stringify({
                query: slice.items.map(
                    (item) =>
                        (item.tab_product as FilledContentRelationshipField).id,
                ),
            }),
            headers: {
                'Content-Type': 'application/json',
                charset: 'utf-8',
            },
        };
        fetch('/prismic/api/get-type-by-ids', requestOptions)
            .then((response) => response.json())
            .then(
                (data) => data.results.length && setProductData(data.results),
            );
    }, [slice.items]);

    return (
        <section
            style={{
                backgroundImage: `url(${slice.primary.background.url})`,
            }}
            className={classNames('p-4', 'bg-cover', 'bg-center')}
        >
            <div
                style={{
                    borderColor: slice.primary.border_colour ?? undefined,
                }}
                className={styles['wrapper']}
            >
                <div className="flex flex-wrap justify-center">
                    {productData && productData[tabActive] ? (
                        <div className={styles['top']}>
                            <Badge variant="tag-medium">
                                {productData[tabActive].data.badge}
                            </Badge>
                            <span>
                                {productData[tabActive].data.icon.url ? (
                                    <PrismicNextImage
                                        field={productData[tabActive].data.icon}
                                        width={40}
                                        height={40}
                                    />
                                ) : null}
                            </span>
                        </div>
                    ) : null}
                    <Heading level="h1" tag="h" centered>
                        {slice.primary.title}
                    </Heading>
                    {productData && productData.length > 1 && (
                        <div className={styles['tabs']}>
                            {productData.map((_item, i) => {
                                return (
                                    <button
                                        key={i}
                                        onClick={() => {
                                            setTabActive(i);
                                            trackClick(EventNames.TAB_CLICKED, {
                                                pageType: context.pageType,
                                                label:
                                                    slice.items[i].tab_name ||
                                                    '',
                                                location: slice.slice_type,
                                            });
                                        }}
                                        className={classNames(
                                            styles['tab'],
                                            'border-b-2 px-4 py-2',
                                        )}
                                        data-tab-active={tabActive === i}
                                    >
                                        {slice.items[i].tab_name}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {productData && (
                        <Product
                            product={productData[tabActive].data}
                            slice={slice}
                            context={context}
                        />
                    )}
                </div>
            </div>
        </section>
    );
};

type ProductProps = {
    product: Content.ProductDocumentData;
    slice: Content.HeroProductSlice;
    context: {
        pageType: string;
    };
};

const Product = ({ product, slice, context }: ProductProps) => {
    return (
        <>
            <div className={styles['product']}>
                <div className={styles['image']}>
                    {product.main_image.square.url ? (
                        <PrismicNextImage field={product.main_image.square} />
                    ) : product.main_image.url ? (
                        <PrismicNextImage field={product.main_image} />
                    ) : null}
                    {product.active_ingredients && (
                        <div className={styles['active-ingredients']}>
                            {`${product.active_ingredients} active ingredient`}
                            {product.active_ingredients > 1 && 's'}
                        </div>
                    )}
                </div>
                <div className={styles['content']}>
                    {product.usp && (
                        <div className={styles['usp']}>
                            <span>
                                <FontAwesomeIcon icon={faCheck} />
                                {product.usp}
                            </span>
                        </div>
                    )}
                    <div className={styles['slices']}>
                        {product.slices.map(
                            (
                                slice: ProductDocumentDataSlicesSlice,
                                i: number,
                            ) => {
                                if (slice.slice_type === 'table_with_columns') {
                                    return (
                                        <TableWithColumns
                                            key={i}
                                            slice={slice}
                                            slices={[]}
                                            index={i}
                                            context={context}
                                        />
                                    );
                                } else if (slice.slice_type === 'list') {
                                    return (
                                        <List
                                            key={i}
                                            slice={slice}
                                            slices={[]}
                                            index={i}
                                            context={context}
                                        />
                                    );
                                } else {
                                    return null;
                                }
                            },
                        )}
                    </div>
                    <div className={styles['bottom']}>
                        <span className={styles['price']}>
                            {product.prefix}
                            {product.prefix ? ' ' : ''}
                            {'$' + product.value}
                            {product.frequency}
                        </span>
                        {product.cta_label && product.cta_link ? (
                            <Button
                                text={product.cta_label}
                                href={product.cta_link}
                                as="a"
                                onClick={() => {
                                    trackClick(EventNames.BUTTON_CLICKED, {
                                        pageType: context.pageType,
                                        label: product.cta_label || '',
                                        link: product.cta_link || '',
                                        location: slice.slice_type,
                                    });
                                }}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
            {product.references && (
                <span className={styles['references']}>
                    <PrismicRichText field={product.references} />
                </span>
            )}
        </>
    );
};

export default HeroProduct;
